/**
 * sessionIdentityStorage — Issue #317.
 *
 * Stores the current admin's `tenantId` / `orgId` in `localStorage` so the
 * auto-refresh helper can build the `X-Tenant-Id` / `X-Org-Id` headers that
 * `/api/v1/auth/refresh` requires. These ids are NOT secrets — they are the
 * same values returned by `/auth/me` and `/auth/login` — so they are safe to
 * keep alongside the access token under the existing browser-storage trust
 * boundary.
 *
 * 红线 (AI GUIDE §6):
 *   - This module NEVER stores access tokens, refresh tokens, JWT material,
 *     cookies, passwords, or PII. Only the `tenantId` / `orgId` pair, which
 *     the backend already returns in the public `/auth/me` response.
 *   - The refresh-token row stays in an HttpOnly cookie; nothing here weakens
 *     that boundary.
 */
const TENANT_ID_KEY = '__jinzhuan_session_tenant_id__';
const ORG_ID_KEY = '__jinzhuan_session_org_id__';

export interface SessionIdentity {
  readonly tenantId: string;
  readonly orgId: string;
}

export function getStoredSessionIdentity(): SessionIdentity | null {
  try {
    const tenantId = localStorage.getItem(TENANT_ID_KEY);
    const orgId = localStorage.getItem(ORG_ID_KEY);
    if (!tenantId || !orgId) return null;
    return { tenantId, orgId };
  } catch {
    return null;
  }
}

export function storeSessionIdentity(identity: SessionIdentity): void {
  try {
    localStorage.setItem(TENANT_ID_KEY, identity.tenantId);
    localStorage.setItem(ORG_ID_KEY, identity.orgId);
  } catch {
    // localStorage unavailable (private mode / quota); auto-refresh just
    // falls back to "no identity" and the existing 401 path takes over.
  }
}

export function clearStoredSessionIdentity(): void {
  try {
    localStorage.removeItem(TENANT_ID_KEY);
    localStorage.removeItem(ORG_ID_KEY);
  } catch {
    // ignore — nothing to clean up
  }
}
